import React from 'react';
import {
  Box, Button, Typography, TextField, Select, MenuItem,
  FormControl,
} from '@mui/material';
import { getClientAddress, getComunes, postClientAddress } from '../../queries/authorization';
import BancameAlert from '../Alert';
import BancameBackdrop from '../BancameBackdrop';

interface AddressFormProps {
  token: string;
  onNext: () => void;
  onError: (error: string) => void;
}

export default function AddressForm({ token, onNext, onError }: AddressFormProps) {
  const [address, setAddress] = React.useState({
    addressLine1: '',
    addressLine2: '',
    communeId: '',
    zipcode: 0,
    communeName: '',
    regionId: '',
    regionName: '',
  });
  const [fieldErrors, setFieldErrors] = React.useState({
    addressLine1: false,
    addressLine2: false,
    regionId: false,
    communeId: false,
  });
  const [comunes, setComunes] = React.useState<any[]>([]);
  const [regiones, setRegiones] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(true);
  const [hasAddress, setHasAddress] = React.useState(false);

  const fetchAddressAndComunes = async () => {
    setLoading(true);
    try {
      const { data } = await getClientAddress(token);
      if (data && data.address && Object.keys(data.address).length > 0) {
        setAddress(data.address);
        setHasAddress(true);
        setIsEditing(false);
      }
      const { data: comunesData } = await getComunes(token);
      if (comunesData) {
        setComunes(comunesData.communes);
        setRegiones(comunesData.regions);
      }
    } catch (e) {
      onError('Error al obtener la dirección o las comunas');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name: string, value: string) => {
    if (name === 'addressLine1') {
      return value.length < 2;
    }
    if (name === 'addressLine2') {
      return value.length < 3 && value !== '';
    }
    if (name === 'regionId' || name === 'communeId') {
      return value === '';
    }
    return false;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddress({
      ...address,
      [name]: value,
    });
    setFieldErrors({
      ...fieldErrors,
      [name]: validateField(name, value),
    });
  };

  const handleBlur = (name: string, value: string) => {
    setFieldErrors({
      ...fieldErrors,
      [name]: validateField(name, value),
    });
  };

  const handleEdit = () => {
    if (isEditing) {
      const addressLine1Error = address.addressLine1.length < 2;
      const addressLine2Error = address.addressLine2 !== '' && address.addressLine2.length < 3;
      const communeIdError = address.communeId === '';
      const regionIdError = address.regionId === '';
      setFieldErrors({
        addressLine1: addressLine1Error,
        addressLine2: addressLine2Error,
        regionId: regionIdError,
        communeId: communeIdError,
      });

      if (addressLine1Error || addressLine2Error || communeIdError) {
        setError(true);
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }
      setIsEditing(false);
      return;
    }
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const addressLine1Error = address.addressLine1 === '';
    const communeIdError = address.communeId === '';
    setFieldErrors({
      ...fieldErrors,
      addressLine1: addressLine1Error,
      communeId: communeIdError,
    });
    if (addressLine1Error || communeIdError) {
      setError(true);
      setTimeout(() => {
        setError(false);
      }, 2000);
      setLoading(false);
      return;
    }
    const addressToPost = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      communeId: address.communeId,
      zipcode: address.zipcode,
    };
    try {
      await postClientAddress(token, addressToPost);
      onNext();
    } catch (e) {
      onError('Error al enviar la dirección');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAddressAndComunes();
  }, [token]);

  const getHelperText = (fieldName: string) => {
    if (!fieldErrors[fieldName as keyof typeof fieldErrors]) return '';
    switch (fieldName) {
      case 'addressLine1': return 'La calle es requerida';
      case 'addressLine2': return 'El depto/condomio/piso debe tener al menos 3 caracteres';
      case 'regionId': return 'La región es requerida';
      case 'communeId': return 'La comuna es requerida';
      default: return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        width: '340px',
      }}
      >
        <BancameBackdrop openBackdrop={loading} loadingText="Cargando..." type="loading" />
      </Box>
    );
  }
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 'sm',
    }}
    >
      <Typography variant="h6" gutterBottom>
        {hasAddress ? 'Confirma tu dirección' : 'Ingresa tu dirección'}
      </Typography>
      <Typography variant="body2" gutterBottom textAlign="center" sx={{ marginBottom: 3 }}>
        Confirma tu dirección domiciliaria. En caso que la información
        sea incorrecta o esté incompleta, por favor edita e ingresa tu dirección.
      </Typography>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: 2,
        width: '100%',
      }}
      >
        <Typography variant="body2">
          Dirección (Calle y Número)*
        </Typography>
        <TextField
          id="addressLine1"
          name="addressLine1"
          variant="outlined"
          disabled={!isEditing}
          size="small"
          fullWidth
          margin="dense"
          placeholder="Calle 123"
          value={address.addressLine1}
          autoFocus
          onChange={(e) => {
            handleChange(e as React.ChangeEvent<HTMLInputElement>);
          }}
          onBlur={() => handleBlur('addressLine1', address.addressLine1)}
          sx={{
            marginTop: 0.5,
            marginBottom: 2,
          }}
          error={fieldErrors.addressLine1}
          helperText={getHelperText('addressLine1')}
        />
        <Typography variant="body2">
          Depto/Condomio/Piso (Opcional)
        </Typography>
        <TextField
          id="addressLine2"
          name="addressLine2"
          size="small"
          variant="outlined"
          fullWidth
          margin="dense"
          disabled={!isEditing}
          value={address.addressLine2}
          placeholder="Depto 22"
          onChange={(e) => {
            handleChange(e as React.ChangeEvent<HTMLInputElement>);
          }}
          onBlur={() => handleBlur('addressLine2', address.addressLine2)}
          sx={{
            marginTop: 0.5,
            marginBottom: 2,
          }}
          error={fieldErrors.addressLine2}
          helperText={getHelperText('addressLine2')}
        />
        <Typography variant="body2">
          Región*
        </Typography>
        <FormControl fullWidth error={fieldErrors.regionId}>
          <Select
            id="regionId"
            name="regionId"
            value={address.regionId}
            disabled={!isEditing}
            onChange={(e) => {
              handleChange(e as React.ChangeEvent<HTMLInputElement>);
            }}
            onBlur={() => handleBlur('regionId', address.regionId)}
            sx={{
              marginTop: 0.5,
              marginBottom: fieldErrors.regionId ? 0 : 2,
            }}
            size="small"
            displayEmpty
            renderValue={
                  address.regionId === ''
                    ? () => (
                      <Typography color="text.secondary">
                        Seleccionar región...
                      </Typography>
                    )
                    : undefined
                }
          >
            {regiones.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                {region.name}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.regionId && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mb: 2 }}>
              {getHelperText('regionId')}
            </Typography>
          )}
        </FormControl>
        <Typography variant="body2">
          Comuna*
        </Typography>
        <FormControl fullWidth error={fieldErrors.communeId}>
          <Select
            id="communeId"
            name="communeId"
            value={address.communeId}
            disabled={!address.regionId || !isEditing}
            onChange={(e) => {
              handleChange(e as React.ChangeEvent<HTMLInputElement>);
            }}
            onBlur={() => handleBlur('communeId', address.communeId)}
            size="small"
            displayEmpty
            renderValue={
                  address.communeId === ''
                    ? () => (
                      <Typography color="text.secondary">
                        Seleccionar comuna...
                      </Typography>
                    )
                    : undefined
                }
            sx={{
              marginTop: 0.5,
              marginBottom: fieldErrors.communeId ? 0 : 2,
            }}
          >
            {comunes.map((comuna) => {
              if (comuna.regionId === address.regionId) {
                return (
                  <MenuItem key={comuna.id} value={comuna.id}>
                    {comuna.name}
                  </MenuItem>
                );
              }
              return null;
            })}
          </Select>
          {fieldErrors.communeId && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mb: 2 }}>
              {getHelperText('communeId')}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleEdit}
        sx={{ marginBottom: 2 }}
        disabled={isEditing && Object.values(fieldErrors).some((err) => err)}
      >
        {isEditing ? 'Guardar' : 'Editar dirección'}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isEditing}
      >
        Confirmar
      </Button>
      <BancameAlert
        openAlert={error}
        successMessage="Dirección actualizada correctamente"
        errorMessage="Por favor, ingresa todos los campos requeridos"
        error
      />
    </Box>
  );
}
